import { useEffect, useId, useState } from 'react';
import { Button, Checkbox, Select, Switch, useToast } from '@vd/ui';
import type { Column, ProjectTicketType } from '@vd/types';
import { api, type JiraAutomation } from '../../../lib/api';
import { TypeMarker } from '../../../features/ticketTypes/TypeMarker';
import styles from '../Settings.module.css';
import auto from './JiraAutoSendSection.module.css';

type Draft = Omit<JiraAutomation, 'enabledAt'>;
const OFF: Draft = { includeWidgetReports: false, ticketTypeIds: [], afterSendColumnId: null, archiveAfterSend: false };

/**
 * Sending every new ticket to Jira by itself (Jason, 18 Oct), under a mapped
 * project's mapping: for a team that keeps everything in Jira. What it sends
 * and when is the server's (jira/autoSend.ts); this says so in plain words and
 * saves the choices. Off, and the options hide.
 */
export function JiraAutoSendSection({ projectId, ticketTypes }: { projectId: string; ticketTypes: ProjectTicketType[] }) {
  const toast = useToast();
  const switchId = useId();
  const [saved, setSaved] = useState<JiraAutomation | null | undefined>(undefined);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [on, setOn] = useState(false);
  const [draft, setDraft] = useState<Draft>(OFF);
  // Every kind, or the ones ticked: its own state, because an empty list is
  // what the server reads as every kind, and unticking the last one must not
  // turn a narrow setting into the widest (Codex and z.ai review round 1).
  const [typeMode, setTypeMode] = useState<'all' | 'chosen'>('all');
  const [columns, setColumns] = useState<Column[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    void Promise.all([api.getJiraAutomation(projectId), api.listColumns(projectId)]).then(([result, projectColumns]) => {
      if (cancelled) return;
      setSaved(result.automation);
      setOn(result.automation !== null);
      setDraft(result.automation ? {
        includeWidgetReports: result.automation.includeWidgetReports, ticketTypeIds: result.automation.ticketTypeIds,
        afterSendColumnId: result.automation.afterSendColumnId, archiveAfterSend: result.automation.archiveAfterSend,
      } : OFF);
      setTypeMode(result.automation && result.automation.ticketTypeIds.length > 0 ? 'chosen' : 'all');
      setColumns(projectColumns);
    }).catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [projectId, attempt]);

  if (failed) {
    return <section className={styles.section}>
      <h2 className={auto.title}>Automatically send new tickets</h2>
      <p role="alert">Could not load this project's auto-send setting.</p>
      <Button variant="secondary" onClick={() => setAttempt((count) => count + 1)}>Retry</Button>
    </section>;
  }
  if (saved === undefined) return null;

  const allTypes = typeMode === 'all';
  const noneChosen = !allTypes && draft.ticketTypeIds.length === 0;
  const visibleTypes = ticketTypes.filter((type) => !type.hidden || draft.ticketTypeIds.includes(type.id));
  const toggleType = (id: string, checked: boolean) => setDraft((now) => ({
    ...now, ticketTypeIds: checked ? [...now.ticketTypeIds, id] : now.ticketTypeIds.filter((typeId) => typeId !== id),
  }));
  const changed = on !== (saved !== null) || (on && saved !== null && JSON.stringify(draft) !== JSON.stringify({
    includeWidgetReports: saved.includeWidgetReports, ticketTypeIds: saved.ticketTypeIds,
    afterSendColumnId: saved.afterSendColumnId, archiveAfterSend: saved.archiveAfterSend,
  }));

  const save = async () => {
    setBusy(true);
    try {
      const result = await api.saveJiraAutomation(projectId, on ? { enabled: true, ...draft } : { enabled: false });
      setSaved(result.automation);
      toast.push({ variant: 'success', title: on ? 'Auto-send is on' : 'Auto-send is off' });
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Could not save auto-send', description: (e as Error).message });
    } finally { setBusy(false); }
  };

  return <section className={styles.section} aria-labelledby={`${switchId}-title`}>
    <div className={auto.head}>
      <div>
        <h2 id={`${switchId}-title`} className={auto.title}>Automatically send new tickets</h2>
        <p className={auto.help}>
          Every new ticket in this project goes to Jira by itself, once its AI title is written (or after a minute without one).
          Tickets from before you turn this on stay where they are, a ticket is never sent twice, and one taken out of Jira is never put back.
        </p>
      </div>
      <Switch id={switchId} checked={on} onChange={(event) => setOn(event.target.checked)} disabled={busy}
        aria-labelledby={`${switchId}-title`} />
    </div>

    {on && <div className={auto.options}>
      <label className={auto.check}>
        <Checkbox checked={draft.includeWidgetReports} disabled={busy}
          onChange={(event) => setDraft((now) => ({ ...now, includeWidgetReports: event.target.checked }))} />
        <span>
          Include reports from the website widget
          <span className={auto.note}>Off, they stay in this project for your team to check first.</span>
        </span>
      </label>

      {visibleTypes.length > 0 && <fieldset className={auto.types}>
        <legend className={auto.legend}>Which tickets</legend>
        <label className={auto.check}>
          <Checkbox checked={allTypes} disabled={busy}
            onChange={(event) => {
              setTypeMode(event.target.checked ? 'all' : 'chosen');
              setDraft((now) => ({ ...now, ticketTypeIds: event.target.checked ? [] : visibleTypes.map((type) => type.id) }));
            }} />
          <span>Every kind of ticket</span>
        </label>
        {!allTypes && <div className={auto.typeList}>
          {visibleTypes.map((type) => (
            <label key={type.id} className={auto.check}>
              <Checkbox checked={draft.ticketTypeIds.includes(type.id)} disabled={busy}
                onChange={(event) => toggleType(type.id, event.target.checked)} />
              <span className={auto.typeName}><TypeMarker glyph={type.glyph} height={16} />{type.name}</span>
            </label>
          ))}
          {noneChosen
            ? <span className={auto.warn} role="alert">Choose at least one kind, or every kind.</span>
            : <span className={auto.note}>Tickets with no type are sent only with every kind chosen.</span>}
        </div>}
      </fieldset>}

      <fieldset className={auto.types}>
        <legend className={auto.legend}>Once it is in Jira</legend>
        <div className={auto.row}>
          <span>Move it to</span>
          <Select value={draft.afterSendColumnId ?? ''} disabled={busy} aria-label="Column to move a sent ticket to"
            onChange={(event) => setDraft((now) => ({ ...now, afterSendColumnId: event.target.value || null }))}>
            <option value="">Leave it where it is</option>
            {columns.map((column) => <option key={column.id} value={column.id}>{column.name}</option>)}
          </Select>
        </div>
        <label className={auto.check}>
          <Checkbox checked={draft.archiveAfterSend} disabled={busy}
            onChange={(event) => setDraft((now) => ({ ...now, archiveAfterSend: event.target.checked }))} />
          <span>
            Archive it
            <span className={auto.note}>It stays linked to its Jira issue and can be restored from the archive.</span>
          </span>
        </label>
        <span className={auto.note}>Not if someone on your team has moved it in the meantime.</span>
      </fieldset>
    </div>}

    <div className={auto.actions}>
      <Button variant="primary" disabled={!changed || busy || (on && noneChosen)} loading={busy}
        onClick={() => void save()}>Save auto-send</Button>
    </div>
  </section>;
}
